import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudNote } from '../../types'
import { NOTE_TYPE_LABELS } from '../../types'
import { listCloudNotes, upsertCloudNote, deleteCloudNote, deleteBlob } from '../../services/api'
import { formatDate, EmptyState, DetailRow, cardStyle, detailPanelStyle, listAreaStyle, refreshBtnStyle, tabStyle } from './shared'
import { ConfirmDialog, ModalDialog, FormField, SaveButton, DeleteButton, CreateButton, InlineInput } from './CrudHelpers'
import { AttachmentList, pickAndUploadAttachments, type StoredAttachment } from './AttachmentControls'
import { ReportContentDialog, isAiCreatedNote } from '../ReportContentDialog'

/** Normalize camelCase fields from Flutter to snake_case used by the web client */
function normalizeNote(n: CloudNote): CloudNote {
  const r = n as any
  if (r.isAiGeneratedDocument != null && n.is_ai_generated_document == null) n.is_ai_generated_document = r.isAiGeneratedDocument
  if (r.isAiGeneratedImage != null && n.is_ai_generated_image == null) n.is_ai_generated_image = r.isAiGeneratedImage
  if (r.isGeneratedPdf != null && n.is_generated_pdf == null) n.is_generated_pdf = r.isGeneratedPdf
  if (r.isApp != null && n.is_app == null) n.is_app = r.isApp
  if (r.createdAt != null && n.created_at == null) n.created_at = r.createdAt
  if (r.lastUpdatedAt != null && n.last_updated_at == null) n.last_updated_at = r.lastUpdatedAt
  if (n.attachments) {
    for (const att of n.attachments) {
      if (att.blobKey && !att.blob_key) att.blob_key = att.blobKey
      if (att.filePath && !att.file_path) att.file_path = att.filePath
    }
  }
  return n
}

/** Exclude anything that belongs in FilesView or AppsView.
 *  Regular notes include text/mixed notes, even with user-added image attachments.
 *  Only AI-generated images/documents and standalone media belong in FilesView. */
const isRegularNote = (n: CloudNote) =>
  !n.is_ai_generated_document && !n.is_ai_generated_image && !n.is_generated_pdf && !n.is_app

const CATEGORIES = [
  { label: 'All', filter: (n: CloudNote) => (n.type === 0 || n.type === 2 || n.type === 3) && isRegularNote(n) },
  { label: 'Text', filter: (n: CloudNote) => n.type === 0 && isRegularNote(n) },
  { label: 'Mixed', filter: (n: CloudNote) => (n.type === 2 || n.type === 3) && isRegularNote(n) },
]

export function NotesView({ focusId }: { focusId?: string } = {}) {
  const [allNotes, setAllNotes] = useState<CloudNote[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedNote, setSelectedNote] = useState<CloudNote | null>(null)
  const [editingNote, setEditingNote] = useState<CloudNote | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [categoryIdx, setCategoryIdx] = useState(0)
  const [showCreate, setShowCreate] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [reportedId, setReportedId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setAllNotes((await listCloudNotes()).map(normalizeNote))
    } catch (e) {
      console.error('Failed to load notes:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Auto-select the focused note (deep-link from search). If the note is in a
  // category other than the active one, switch to "All" so it's visible.
  useEffect(() => {
    if (!focusId || allNotes.length === 0) return
    const match = allNotes.find((n) => n.id === focusId)
    if (!match) return
    if (selectedNote?.id !== match.id) {
      const inFiltered = CATEGORIES[categoryIdx].filter(match)
      if (!inFiltered) {
        const allIdx = CATEGORIES.findIndex((c) => c.label === 'All')
        if (allIdx >= 0 && allIdx !== categoryIdx) setCategoryIdx(allIdx)
      }
      setSelectedNote(match)
      setEditingNote({ ...match })
      setIsDirty(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, allNotes])

  const filtered = CATEGORIES[categoryIdx].filter
    ? allNotes.filter(CATEGORIES[categoryIdx].filter)
    : allNotes

  const handleSelect = (note: CloudNote) => {
    if (selectedNote?.id === note.id) {
      setSelectedNote(null)
      setEditingNote(null)
      setIsDirty(false)
    } else {
      setSelectedNote(note)
      setEditingNote({ ...note })
      setIsDirty(false)
    }
  }

  const updateField = (field: string, value: unknown) => {
    if (!editingNote) return
    setEditingNote({ ...editingNote, [field]: value })
    setIsDirty(true)
  }

  const handleSave = async () => {
    if (!editingNote) return
    setSaving(true)
    try {
      const updated = { ...editingNote, last_updated_at: new Date().toISOString() }
      await upsertCloudNote(updated)
      setAllNotes((prev) => prev.map((n) => n.id === updated.id ? updated : n))
      setSelectedNote(updated)
      setEditingNote({ ...updated })
      setIsDirty(false)
    } catch (e) {
      console.error('Failed to save note:', e)
    } finally {
      setSaving(false)
    }
  }

  /**
   * Persist a fresh batch of attachments to cloud immediately. Without this,
   * uploaded blobs would sit orphaned in R2 until the user remembered to
   * click Save — and a sync-down to mobile in the meantime would never see
   * the new attachment metadata. The blob is already in R2 from the upload
   * call, so this is a tiny incremental DB write that also captures any
   * other dirty edits the user has in flight.
   */
  const persistAttachmentChange = async (added: StoredAttachment[]) => {
    if (!editingNote || added.length === 0) return
    const merged = [...((editingNote.attachments as unknown as StoredAttachment[]) || []), ...added]
    await persistAttachmentsList(merged)
  }

  /**
   * Persist the full attachment list and clean up any R2 blobs whose
   * attachments are no longer present. Used by AttachmentList's onChange:
   * when the user clicks the X on an attachment row, this fires the
   * cloud upsert and the blob deletion in one pass — no Save click
   * required, and no orphaned R2 blobs left behind.
   */
  const persistAttachmentsList = async (next: StoredAttachment[]) => {
    if (!editingNote) return
    const previous = (editingNote.attachments as unknown as StoredAttachment[]) || []
    const nextIds = new Set(next.map((a) => a.id))
    const removed = previous.filter((a) => !nextIds.has(a.id))
    const updated = {
      ...editingNote,
      attachments: next,
      last_updated_at: new Date().toISOString(),
    } as unknown as CloudNote
    await upsertCloudNote(updated)
    setAllNotes((prev) => prev.map((n) => n.id === updated.id ? updated : n))
    setSelectedNote(updated)
    setEditingNote({ ...updated })
    setIsDirty(false)
    // Best-effort R2 cleanup for removed attachments. Failures are silent
    // (logged inside deleteBlob) since metadata removal is what the user
    // sees; an orphan blob is invisible and can be reaped later.
    for (const att of removed) {
      if (att.blobKey) void deleteBlob(att.blobKey)
    }
  }

  const handleDelete = async () => {
    if (!selectedNote) return
    try {
      await deleteCloudNote(selectedNote.id)
      setAllNotes((prev) => prev.filter((n) => n.id !== selectedNote.id))
      setSelectedNote(null)
      setEditingNote(null)
      setConfirmDelete(false)
    } catch (e) {
      console.error('Failed to delete note:', e)
    }
  }

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Note list */}
      <div style={listAreaStyle}>
        <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
          {CATEGORIES.map((cat, i) => {
            const count = allNotes.filter(cat.filter).length
            return (
              <button
                key={cat.label}
                onClick={() => { setCategoryIdx(i); setSelectedNote(null); setEditingNote(null); setIsDirty(false) }}
                style={tabStyle(categoryIdx === i)}
              >
                {cat.label} ({count})
              </button>
            )
          })}
          <span style={{ flex: 1 }} />
          <CreateButton label="New Note" onClick={() => setShowCreate(true)} />
          <button onClick={load} style={{ ...refreshBtnStyle, margin: '0 8px' }} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>}
          {!loading && filtered.length === 0 && <EmptyState label="No notes found" />}
          {!loading && filtered.map((note) => (
            <div
              key={note.id}
              onClick={() => handleSelect(note)}
              style={{
                ...cardStyle,
                cursor: 'pointer',
                background: selectedNote?.id === note.id ? 'rgba(45,165,160,0.15)' : cardStyle.background,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, fontWeight: 500 }}>{note.title || 'Untitled'}</span>
                <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)' }}>
                  {NOTE_TYPE_LABELS[note.type ?? 0]}
                </span>
              </div>
              {Array.isArray(note.tags) && note.tags.length > 0 && (
                <div style={{ fontSize: 11, color: '#4fc3f7', marginTop: 4 }}>{note.tags.join(', ')}</div>
              )}
              <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.35)', marginTop: 4 }}>
                {formatDate(note.last_updated_at || note.created_at)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editable detail panel */}
      {editingNote && (
        <div style={{ ...detailPanelStyle, width: 'auto', flex: 1 }}>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: 'rgba(250,250,250,0.5)' }}>Edit Note</span>
            <SaveButton dirty={isDirty} saving={saving} onClick={handleSave} />
            {/* Report notes the AI created (Google Play AI content policy) */}
            {selectedNote && isAiCreatedNote(selectedNote) && (
              <button
                onClick={() => setReportOpen(true)}
                disabled={reportedId === selectedNote.id}
                style={refreshBtnStyle}
                title="Report inappropriate content"
              >
                {reportedId === selectedNote.id ? '✓ Reported' : '\u{1F6A9} Report'}
              </button>
            )}
            <DeleteButton onClick={() => setConfirmDelete(true)} />
            <button onClick={() => { setSelectedNote(null); setEditingNote(null); setIsDirty(false) }} style={refreshBtnStyle}>X</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Title</label>
              <InlineInput
                value={editingNote.title || ''}
                onChange={(v) => updateField('title', v)}
                placeholder="Note title"
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Content</label>
              <InlineInput
                type="textarea"
                value={editingNote.content || ''}
                onChange={(v) => updateField('content', v)}
                placeholder="Note content"
                style={{ minHeight: 200 }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Tags</label>
              <InlineInput
                value={(Array.isArray(editingNote.tags) ? editingNote.tags : []).join(', ')}
                onChange={(v) => updateField('tags', v ? v.split(',').map((t) => t.trim()).filter(Boolean) : [])}
                placeholder="tag1, tag2, ..."
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Attachments</label>
              <AttachmentList
                attachments={(editingNote.attachments as unknown as StoredAttachment[]) || []}
                uploading={uploading}
                // onChange fires when a row's X button is clicked. We
                // persist the new list immediately and clean R2 of any
                // removed blob, so the user never has to remember to
                // click Save just to drop an attachment.
                onChange={(next) => { void persistAttachmentsList(next) }}
                onAddImages={async () => {
                  setUploading(true)
                  try {
                    const added = await pickAndUploadAttachments('notes', editingNote.id, true, false)
                    if (added.length === 0) return
                    await persistAttachmentChange(added)
                  } finally {
                    setUploading(false)
                  }
                }}
                onAddDocs={async () => {
                  setUploading(true)
                  try {
                    const added = await pickAndUploadAttachments('notes', editingNote.id, false, true)
                    if (added.length === 0) return
                    await persistAttachmentChange(added)
                  } finally {
                    setUploading(false)
                  }
                }}
              />
            </div>
            {editingNote.transcription && (
              <div style={{ marginBottom: 12 }}>
                <label style={fieldLabelStyle}>Transcription</label>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: 'rgba(250,250,250,0.7)', background: 'rgba(0,34,51,0.6)', padding: 8, borderRadius: 6 }}>
                  {editingNote.transcription}
                </div>
              </div>
            )}
            <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
              <DetailRow label="Type" value={NOTE_TYPE_LABELS[editingNote.type ?? 0]} />
              <DetailRow label="Created" value={formatDate(editingNote.created_at)} />
              <DetailRow label="Updated" value={formatDate(editingNote.last_updated_at)} />
            </div>
          </div>
        </div>
      )}

      {/* Create note modal */}
      {showCreate && (
        <CreateNoteModal
          onClose={() => setShowCreate(false)}
          onCreate={async (note) => {
            await upsertCloudNote(note)
            setAllNotes((prev) => [note, ...prev])
            setShowCreate(false)
          }}
        />
      )}

      {reportOpen && editingNote && (
        <ReportContentDialog
          kind="note"
          aiContent={`${editingNote.title || ''}\n\n${editingNote.content || ''}`}
          messageId={editingNote.id}
          onClose={(sent) => {
            setReportOpen(false)
            if (sent) setReportedId(editingNote.id)
          }}
        />
      )}

      {/* Delete confirm */}
      {confirmDelete && selectedNote && (
        <ConfirmDialog
          message={`Delete note "${selectedNote.title || 'Untitled'}"?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}

// ── Create Note Modal ─────────────────────────────────────────────

function CreateNoteModal({ onClose, onCreate }: {
  onClose: () => void
  onCreate: (note: CloudNote) => Promise<void>
}) {
  // Pre-generate the note id so attachment uploads can happen *before* save —
  // R2 keys the blob on the entity id, and the matching `attachments` array
  // gets written into cloud_notes when the user finally clicks Create. If
  // they bail out without saving, the orphan blob behaves like any other
  // unattached upload (mirrors mobile's "edit then discard" path).
  const [noteId] = useState(() => crypto.randomUUID())
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [type, setType] = useState('0')
  const [tags, setTags] = useState('')
  const [creating, setCreating] = useState(false)
  const [attachments, setAttachments] = useState<StoredAttachment[]>([])
  const [uploading, setUploading] = useState(false)

  const handleCreate = async () => {
    if (!title.trim()) return
    setCreating(true)
    try {
      const now = new Date().toISOString()
      const note: CloudNote = {
        id: noteId,
        title: title.trim(),
        content: content.trim() || undefined,
        // Bump the type to "Mixed" when the user attaches at least one file
        // so the note shows up under the Mixed tab too — mirrors the
        // mobile-side rule applied in `note_model.dart`.
        type: attachments.length > 0 && Number(type) === 0 ? 3 : Number(type),
        tags: tags ? tags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
        attachments: attachments.length > 0 ? (attachments as unknown as CloudNote['attachments']) : undefined,
        created_at: now,
        last_updated_at: now,
      }
      await onCreate(note)
    } catch (e) {
      console.error('Failed to create note:', e)
    } finally {
      setCreating(false)
    }
  }

  const handlePickImages = async () => {
    setUploading(true)
    try {
      const added = await pickAndUploadAttachments('notes', noteId, true, false)
      if (added.length > 0) setAttachments((prev) => [...prev, ...added])
    } finally {
      setUploading(false)
    }
  }

  const handlePickDocs = async () => {
    setUploading(true)
    try {
      const added = await pickAndUploadAttachments('notes', noteId, false, true)
      if (added.length > 0) setAttachments((prev) => [...prev, ...added])
    } finally {
      setUploading(false)
    }
  }

  return (
    <ModalDialog
      title="New Note"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: 'rgba(250,250,250,0.1)', color: 'rgba(250,250,250,0.7)', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!title.trim() || creating || uploading}
            style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#2DA5A0', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: !title.trim() || creating || uploading ? 0.5 : 1 }}
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </>
      }
    >
      <FormField label="Title" value={title} onChange={setTitle} required placeholder="Note title" />
      <FormField label="Content" value={content} onChange={setContent} type="textarea" placeholder="Note content" />
      <FormField label="Type" value={type} onChange={setType} type="select" options={[{ value: '0', label: 'Text' }, { value: '3', label: 'Mixed' }]} />
      <FormField label="Tags" value={tags} onChange={setTags} placeholder="tag1, tag2, ..." />
      <div style={{ marginTop: 12 }}>
        <label style={{ ...fieldLabelStyle, marginBottom: 6 }}>Attachments</label>
        <AttachmentList
          attachments={attachments}
          uploading={uploading}
          onChange={setAttachments}
          onAddImages={handlePickImages}
          onAddDocs={handlePickDocs}
        />
      </div>
    </ModalDialog>
  )
}

const fieldLabelStyle: Record<string, string | number> = {
  fontSize: 12,
  color: 'rgba(250,250,250,0.5)',
  marginBottom: 4,
  display: 'block',
}
